import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconText from 'shared/design-system/components/TrueImpactIconText';
import {
  faFloppyDisk,
  faPen,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { TrueImpactIndicatorFilesRender } from 'shared/design-system/components/TrueImpactFileRender';
import TrueImpactTextfield, {
  calculateTextareaHeight,
} from 'shared/design-system/components/TrueImpactTextfield';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import { IndicatorSectionKey } from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import {
  getTimePeriodFromString,
  getTimePeriodText,
  IndicatorDetails,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import { HandleDeleteIndicatorData } from './ImpactTableStage';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import {
  getIndicatorDetailsMeasurement,
  getIndicatorDetailsMeasurementType,
} from '../../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import { isGuessMeasurement } from './forecastValidationUtils';
import GuessMeasurementReasonModal from './GuessMeasurementReasonModal';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import IndicatorFileUploadRender, {
  indicatorFileUploadInitialState,
  saveIndicatorFileValue,
} from '../../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUploadRender';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useSaveDetailProperties } from 'shared/providers/IndicatorProvider/hooks';
import TimePeriodButtonSmall from '../../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/TimePeriodButtonSmall';
import { HandleUpdatePropertyData, ModelSaveWarning } from './modelErrors';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

interface ImpactEditableProps {
  isError: boolean;
  isWarning: boolean;
  reportId: number;
  name: HandleUpdatePropertyData['name'];
  title: string;
  value?: HandleUpdatePropertyData['value'];
  hideFile?: boolean;
  file?: TrueImpactFile | string;
  className?: string;
  indicatorKey: HandleUpdatePropertyData['type'];
  sectionKey: IndicatorSectionKey;
  indicator: HandleDeleteIndicatorData;
  handleSave: (data: HandleUpdatePropertyData) => unknown;
  onEditChange: (warning: ModelSaveWarning, isEditing: boolean) => unknown;
}

const errorStyles = { marginBottom: SPACERS.small };
const StyledDiv = styled(TrueImpactText)`
  .impact-editable-container {
    flex: 1;
    .report-file {
      display: flex;
      gap: ${SPACERS.tiny};
      flex-direction: column;
      margin-top: ${SPACERS.tiny};
    }
    .file-upload-render textarea,
    .impact-edit-input {
      font-size: 13px;
      padding-top: ${SPACERS.tiny};
      padding-bottom: ${SPACERS.tiny};
      margin-top: ${SPACERS.tiny};
    }
    .impact-edit-input-inactive {
      height: 0;
      min-height: 0 !important;
      padding: 0;
      margin: 0;
      border: none;
      visibility: hidden;
      display: none;
    }
    .dropdown {
      margin-top: ${SPACERS.tiny};
      .filter_select {
        background-color: ${COLORS.grayLightest};
        .selector_label {
          font-size: 13px;
          font-weight: 400;
        }
      }
    }
    .file-upload-render {
      display: block;
      textarea {
        width: 100%;
        border-radius: ${SPACERS.extraTiny};
        margin-bottom: ${SPACERS.tiny}
        border: 1px solid ${COLORS.grayBase};
      }
      .file-item,
      .uploaded-files,
      .file-upload-area {
        width: 100%;
      }
    }
  }
`;

const defaultFunction = (v: string) => v;
const getMeasurementLabel = (measurement: string): string | JSX.Element => {
  const type = getIndicatorDetailsMeasurementType(measurement);
  if (!type) {
    return measurement;
  }
  return (
    <>
      <strong>{type}</strong> {measurement}
    </>
  );
};
const ImpactEditable: React.FC<ImpactEditableProps> = ({
  isError,
  isWarning,
  reportId,
  file: f,
  hideFile,
  indicatorKey,
  indicator,
  sectionKey,
  name,
  title,
  value,
  className,
  handleSave,
  onEditChange,
}) => {
  const {
    saveRequestState,
    getSourcesAndAssumptions,
    handleUpdateDescription,
  } = useIndicator();
  const [saveDetailProperties] = useSaveDetailProperties();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const [files, setFiles] = useState<TrueImpactFile[]>(
    indicatorFileUploadInitialState(indicator.impactDetailIndicator),
  );
  const [description, setDescription] = useState<string>(
    getSourcesAndAssumptions(indicator.impactDetailIndicator),
  );
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [edit, setEdit] = useState<boolean>(false);
  const [state, setState] = useState<string>((value as string) || '');
  const [guessModal, setGuessModal] = useState<{
    open: boolean;
    measurement: string;
    reason: string;
  }>({ open: false, measurement: '', reason: '' });

  useEffect(() => {
    if (state) return;
    setState((value as string) || '');
  }, [value, state]);

  useEffect(() => {
    if (!description) {
      setDescription(getSourcesAndAssumptions(indicator.impactDetailIndicator));
    }
    if (files.length === 0) {
      setFiles(
        indicatorFileUploadInitialState(indicator.impactDetailIndicator),
      );
    }
  }, [
    indicator.impactDetailIndicator,
    description,
    files.length,
    getSourcesAndAssumptions,
  ]);

  const height = calculateTextareaHeight(
    state.length,
    inputRef.current?.offsetWidth,
  );

  const handleEdit = useCallback(() => {
    setEdit(true);
    onEditChange(
      {
        propertyName: name,
        sectionKey: indicator.sectionKey,
        index: indicator.impactDetailIndex,
        indicationId: indicator.indicatorId,
      },
      true,
    );
  }, [setEdit, onEditChange, indicator, name]);

  const persistEdit = useCallback(
    (level1GuessReason?: string) => {
      const value =
        name === 'file'
          ? JSON.stringify(saveIndicatorFileValue(files, description))
          : state;
      const data: HandleUpdatePropertyData = {
        value,
        sectionKey,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        name: name as any,
        type: indicatorKey,
        indicatorId: indicator.indicatorId,
        impactDetailIndex: indicator.impactDetailIndex,
        impactSummaryIndex: indicator.impactSummaryIndex,
        impactSummaryIndicator: indicator.impactSummaryIndicator,
        impactDetailIndicator: indicator.impactDetailIndicator,
      };

      // For measurement saves with a guess reason, include level1Guess in the same API call
      const isMeasurementWithGuess =
        name === 'measurement' && level1GuessReason;

      const request =
        indicatorKey === 'impactSummary'
          ? handleUpdateDescription(indicator.indicatorId, sectionKey, value)
          : saveDetailProperties(indicator.indicatorId, sectionKey, {
              [name as keyof IndicatorDetails]: value,
              ...(isMeasurementWithGuess
                ? { level1Guess: level1GuessReason }
                : {}),
            } as Parameters<typeof saveDetailProperties>[2]);

      request.then(r => {
        if (!r) return;
        setEdit(false);
        onEditChange(
          {
            propertyName: name,
            sectionKey: indicator.sectionKey,
            index: indicator.impactDetailIndex,
            indicationId: indicator.indicatorId,
          },
          false,
        );
        handleSave(data);

        // If we saved level1Guess, also update it in parent local state
        if (isMeasurementWithGuess) {
          handleSave({
            ...data,
            type: 'impactDetail',
            name: 'level1Guess',
            value: level1GuessReason,
          });
        }
      });
    },
    [
      name,
      state,
      files,
      description,
      indicator,
      sectionKey,
      indicatorKey,
      handleUpdateDescription,
      saveDetailProperties,
      handleSave,
      onEditChange,
    ],
  );

  const handleSaveEdit = useCallback(() => {
    // If saving a measurement that is a Guess type, prompt the user for a
    // reason to be saved in level1Guess.
    if (
      levelOneFeedbackEnabled &&
      indicatorKey === 'impactDetail' &&
      name === 'measurement' &&
      isGuessMeasurement(sectionKey, state)
    ) {
      setGuessModal({ open: true, measurement: state, reason: '' });
      return;
    }
    persistEdit();
  }, [
    indicatorKey,
    name,
    sectionKey,
    state,
    persistEdit,
    levelOneFeedbackEnabled,
  ]);

  const handleConfirmGuessReason = useCallback(
    (reason: string) => {
      setGuessModal(s => ({ ...s, open: false, reason }));
      persistEdit(reason);
    },
    [persistEdit],
  );

  const handleCloseGuessModal = useCallback(() => {
    setGuessModal(s => ({ ...s, open: false, reason: '' }));
  }, []);

  // const handleFileChange = useCallback(
  //   (file: File | undefined) => {
  //     if (!file) {
  //       setFile(undefined);
  //       return;
  //     }
  //     const validatedFile = indicatorFileUploadValidations({
  //       file,
  //       setFile,
  //       handleFileUpload: () => null,
  //     });
  //     if (!validatedFile) {
  //       return;
  //     }
  //     indicatorFileUpload({ file: validatedFile, reportId, setFile });
  //   },
  //   [reportId, indicator, setFile, handleSave],
  // );

  const isFile = name === 'file';
  const isTimePeriod = name === 'timePeriod';
  const isMeasurement = name === 'measurement';
  const error = edit && saveRequestState.type === 'REQUEST_ERROR';

  return (
    <>
      <StyledDiv variant="body-small" className={className}>
        <div className="impact-editable-container">
          <div>
            <strong
              className={mergeClassNames(
                isError && 'error-text-field',
                isWarning && 'warning-text-field',
              )}
            >
              {title}
            </strong>{' '}
            {edit
              ? undefined
              : isFile
              ? description
              : isTimePeriod
              ? `${state} (${getTimePeriodText(
                  getTimePeriodFromString(state),
                )})`
              : state}
          </div>
          {isTimePeriod && edit && (
            <TimePeriodButtonSmall
              selected={getTimePeriodFromString(state)}
              handleChange={setState}
            />
          )}
          {!isTimePeriod && (
            <>
              {!isFile && (
                <>
                  <TrueImpactTextfield
                    maxLength={250}
                    key={`${indicator.indicatorId}-${name}-e`}
                    height={edit ? height : undefined}
                    inputRef={inputRef}
                    isTextarea={true}
                    name={name}
                    isError={error}
                    placeholder={title}
                    value={state}
                    onChange={setState}
                    className={mergeClassNames(
                      'impact-edit-input',
                      (!edit || isMeasurement) && 'impact-edit-input-inactive',
                    )}
                  />
                  {edit && !isMeasurement && (
                    <TrueImpactTextIndicator
                      text={state}
                      minLength={0}
                      maxLength={250}
                      handleClear={() => setState('')}
                    />
                  )}
                </>
              )}
              {edit && isMeasurement && (
                <TrueImpactDropdown
                  native={false}
                  key={`${indicator.indicatorId}-${name}-m`}
                  options={getIndicatorDetailsMeasurement(sectionKey)}
                  value={state}
                  borderColor={error ? 'critical' : undefined}
                  getId={defaultFunction}
                  getLabel={getMeasurementLabel}
                  getSelectorValues={(
                    _o: IndicatorDetails['measurement'][],
                    value: IndicatorDetails['measurement'] | undefined,
                  ) => ({
                    label: value || '-- Select --',
                    isAnySelected: Boolean(value),
                  })}
                  onClear={undefined}
                  onChange={setState}
                  className="dropdown"
                />
              )}
              {!hideFile && edit ? (
                <IndicatorFileUploadRender
                  autoFocus={false}
                  reportId={reportId}
                  description={description}
                  setDescription={setDescription}
                  files={files}
                  className="file-upload-render"
                  setFiles={setFiles}
                />
              ) : isFile ? (
                <TrueImpactIndicatorFilesRender
                  indicator={{
                    ...indicator.impactDetailIndicator,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    file: files as any,
                  }}
                />
              ) : null}
            </>
          )}
        </div>
        {edit ? (
          saveRequestState.type === 'REQUEST_START' ? (
            <TrueImpactIconText
              label="Saving..."
              icon={faSpinnerThird}
              variant="body-small"
              color="grayDark"
              isSpinner={true}
              padding={`0 ${SPACERS.tiny} ${SPACERS.tiny} ${SPACERS.tiny}`}
            />
          ) : (
            <TrueImpactIconText
              label="Save"
              icon={faFloppyDisk}
              variant="body-small"
              padding={`0 ${SPACERS.tiny} ${SPACERS.tiny} ${SPACERS.tiny}`}
              onClick={handleSaveEdit}
            />
          )
        ) : (
          <TrueImpactIconText
            label="Edit"
            icon={faPen}
            variant="body-small"
            padding={`0 ${SPACERS.tiny} ${SPACERS.tiny} ${SPACERS.tiny}`}
            onClick={handleEdit}
          />
        )}
      </StyledDiv>
      {error && (
        <TrueImpactText
          variant="body-small"
          color="critical"
          style={errorStyles}
        >
          Unable to save changes. Please try again.
        </TrueImpactText>
      )}
      <GuessMeasurementReasonModal
        key={
          guessModal.open
            ? `guess-${indicator.indicatorId}-open`
            : `guess-${indicator.indicatorId}-closed`
        }
        open={guessModal.open}
        onClose={handleCloseGuessModal}
        onConfirm={handleConfirmGuessReason}
        measurement={guessModal.measurement}
        initialReason={indicator.impactDetailIndicator.level1Guess || ''}
        indicatorName={indicator.impactDetailIndicator.indicatorName || ''}
      />
    </>
  );
};

export default ImpactEditable;
